import React, { useEffect, useState, useRef, useCallback } from 'react';
import mammoth from 'mammoth';
import * as pdfjs from 'pdfjs-dist';
import './SourcePanel.css';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

function getFileExt(link) {
  const pathPart = link.split('?')[0];
  const pathExt = pathPart.split('.').pop().toLowerCase();
  if (pathExt.length <= 5 && !pathExt.includes('/')) return pathExt;
  try {
    const pathParam = new URL(link).searchParams.get('path') || '';
    const paramExt = pathParam.split('.').pop().toLowerCase();
    if (paramExt.length <= 5) return paramExt;
  } catch {}
  return '';
}

function isDocx(link) {
  const ext = getFileExt(link);
  return ext === 'docx' || ext === 'doc';
}

function isPdf(link) {
  return getFileExt(link) === 'pdf';
}

function Spinner({ label }) {
  return (
    <div className="source-panel-loading">
      <div className="source-panel-spinner" />
      <span>{label || 'Loading…'}</span>
    </div>
  );
}

function FileCard({ file, errorMsg }) {
  const ext = getFileExt(file.link).toUpperCase();
  return (
    <div className="source-panel-file-card">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
           className="source-panel-file-icon">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10 9 9 9 8 9"/>
      </svg>
      <span className="source-panel-file-card-ext">{ext}</span>
      <p className="source-panel-file-card-name">{file.name}</p>
      {errorMsg && <p className="source-panel-file-card-error">{errorMsg}</p>}
    </div>
  );
}

/* ── PDF page rendered to canvas ─────────────────────────── */
function PdfPage({ pdf, pageNumber, containerWidth }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    pdf.getPage(pageNumber).then((page) => {
      if (cancelled || !canvasRef.current) return;
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = (containerWidth || 600) / baseViewport.width;
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      page.render({ canvasContext: canvas.getContext('2d'), viewport });
    });
    return () => { cancelled = true; };
  }, [pdf, pageNumber, containerWidth]);

  return <canvas ref={canvasRef} className="source-panel-pdf-canvas" />;
}

function PdfViewer({ file }) {
  const [pdf, setPdf] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [state, setState] = useState('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Measure container width for responsive canvas sizing
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    ro.observe(containerRef.current);
    setContainerWidth(containerRef.current.clientWidth);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    pdfjs.getDocument({ url: file.link, withCredentials: false }).promise
      .then((doc) => {
        if (cancelled) return;
        setPdf(doc);
        setNumPages(doc.numPages);
        setState('ok');
      })
      .catch((err) => {
        if (cancelled) return;
        const isCors = err?.message?.toLowerCase().includes('fetch') ||
                       err?.name === 'UnknownErrorException';
        setErrorMsg(
          isCors
            ? 'Preview blocked by browser security (CORS). Download to view.'
            : 'PDF could not be previewed.'
        );
        setState('error');
      });
    return () => { cancelled = true; };
  }, [file.link]);

  if (state === 'loading') return <Spinner label="Loading PDF…" />;
  if (state === 'error') return <FileCard file={file} errorMsg={errorMsg} />;

  return (
    <div ref={containerRef} className="source-panel-pdf-pages">
      {Array.from({ length: numPages }, (_, i) => (
        <PdfPage
          key={i}
          pdf={pdf}
          pageNumber={i + 1}
          containerWidth={containerWidth}
        />
      ))}
    </div>
  );
}

/* ── DOCX rendered via mammoth ───────────────────────────── */
function DocxViewer({ file }) {
  const [state, setState] = useState('loading');
  const [html, setHtml] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const load = useCallback(async () => {
    setState('loading');
    try {
      const res = await fetch(file.link);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const buffer = await res.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
      setHtml(result.value);
      setState('ok');
    } catch (err) {
      const isCors = err instanceof TypeError;
      setErrorMsg(
        isCors
          ? 'Preview blocked by browser security (CORS). Download to view.'
          : 'Preview failed. Download to view.'
      );
      setState('error');
    }
  }, [file.link]);

  useEffect(() => { load(); }, [load]);

  if (state === 'loading') return <Spinner label="Converting document…" />;
  if (state === 'error') return <FileCard file={file} errorMsg={errorMsg} />;

  return (
    <div
      className="source-panel-docx-content"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function FileBody({ file }) {
  if (isPdf(file.link))  return <PdfViewer  file={file} />;
  if (isDocx(file.link)) return <DocxViewer file={file} />;
  return <FileCard file={file} />;
}

/* ── Main panel ──────────────────────────────────────────── */
export default function SourcePanel({ isOpen, files, activeIndex, onChangeFile, onClose }) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const activeFile = files[activeIndex] ?? files[0] ?? null;

  return (
    <>
      <div
        className={`source-panel-backdrop${isOpen ? ' visible' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`source-panel${isOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Source file viewer"
      >
        {/* Header */}
        <div className="source-panel-header">
          <span className="source-panel-title" title={activeFile?.name}>
            {activeFile?.name || ''}
          </span>
          <div className="source-panel-header-actions">
            {activeFile && (
              <button
                className="source-panel-download-btn"
                onClick={() => {
                  const a = document.createElement('a');
                  a.href = activeFile.link;
                  a.target = '_blank';
                  a.rel = 'noopener noreferrer';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }}
                title="Download file"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Download
              </button>
            )}
            <button
              className="source-panel-close-btn"
              onClick={onClose}
              title="Close"
              aria-label="Close panel"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        {/* File tabs */}
        {files.length > 1 && (
          <div className="source-panel-tabs" role="tablist">
            {files.map((file, idx) => (
              <button
                key={idx}
                role="tab"
                aria-selected={idx === activeIndex}
                className={`source-panel-tab${idx === activeIndex ? ' active' : ''}`}
                onClick={() => onChangeFile(idx)}
                title={file.name}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                <span className="source-panel-tab-name">{file.name}</span>
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="source-panel-body">
          {activeFile && <FileBody key={activeFile.link} file={activeFile} />}
        </div>
      </div>
    </>
  );
}
